/**
 * Сторінка адміністратора для управління повідомленнями підтримки клієнтів
 * @component AdminSupportPage
 * @file AdminSupportPage.tsx
 * @path frontend/src/pages/AdminSupportPage/AdminSupportPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { Badge } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import SupportChatModal from '../../modals/SupportChatModal/SupportChatModal';
import './AdminSupportPage.css';

interface BusinessInfo {
  name: string;
  city: string;
  address: string;
}

interface SupportMessage {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  user_role: string;
  business_name: string;
  business_city: string;
  business_address: string;
  all_businesses: BusinessInfo[];
  category: string;
  message: string;
  admin_response?: {
    String: string;
    Valid: boolean;
  };
  responded_at?: {
    String: string;
    Valid: boolean;
  };
  status: string;
  created_at: string;
  updated_at: string;
  conversation?: Array<{
    id: string;
    sender_type: string;
    sender_id?: string;
    sender_name?: string;
    message: string;
    created_at: string;
  }>;
}

interface SupportStats {
  new: number;
  in_progress: number;
  resolved: number;
  closed: number;
  total: number;
}

interface SupportSettings {
  phone: string;
  email: string;
  working_hours: string;
}

const AdminSupportPage: React.FC = () => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const [activeTab, setActiveTab] = useState<'messages' | 'settings'>('messages');
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [stats, setStats] = useState<SupportStats>({ new: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<SupportMessage | null>(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [supportSettings, setSupportSettings] = useState<SupportSettings>({
    phone: '',
    email: '',
    working_hours: '',
  });
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);

  const getAdminLastSeenStorageKey = () => {
    return `admin_support_last_seen_at`;
  };

  const readAdminLastSeenAt = () => {
    try {
      const key = getAdminLastSeenStorageKey();
      const raw = localStorage.getItem(key);
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    } catch {
      return 0;
    }
  };

  const countUnreadClientMessagesForThread = (message: SupportMessage) => {
    if (!Array.isArray(message.conversation)) return 0;
    const adminLastSeen = readAdminLastSeenAt();
    let count = 0;
    for (const msg of message.conversation) {
      if (msg?.sender_type === 'client' && new Date(msg?.created_at).getTime() > adminLastSeen) {
        count++;
      }
    }
    return count;
  };

  // Translation helper (not used in this component)

  // Load messages and stats
  const loadData = async () => {
    try {
      const token = localStorage.getItem('auth_token');

      // Load messages
      const messagesResponse = await fetch(`/api/v1/admin/support/messages?status=${statusFilter}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (messagesResponse.ok) {
        const messagesData = await messagesResponse.json();
        setMessages(messagesData.messages || []);
      }

      // Load stats
      const statsResponse = await fetch('/api/v1/admin/support/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        const stats = statsData.stats || { new: 0, in_progress: 0, resolved: 0, closed: 0 };
        const total = (stats.new || 0) + (stats.in_progress || 0) + (stats.resolved || 0) + (stats.closed || 0);
        setStats({ ...stats, total });
      }
    } catch (error) {
      console.error('Error loading support data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle quick status update from table
  const handleQuickStatusUpdate = async (messageId: string, newStatus: string) => {
    if (!messageId || !newStatus) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/admin/support/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: newStatus,
          response: '', // Empty response for quick updates
        }),
      });

      if (response.ok) {
        // Reload data
        await loadData();
        addNotification(language === 'ua' ? 'Статус повідомлення успішно оновлено!' : 'Message status updated successfully!', 'success');
      } else {
        throw new Error('Failed to update message status');
      }
    } catch (error) {
      console.error('Error updating message status:', error);
      addNotification(language === 'ua' ? 'Помилка при оновленні статусу' : 'Error updating status', 'error');
    }
  };

  // Handle sending reply to client without changing status
  const handleSendReply = async (messageId: string, responseText: string) => {
    if (!messageId || !responseText.trim()) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/admin/support/messages/${messageId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          reply: responseText.trim(),
        }),
      });

      if (response.ok) {
        // Reload data and update selected message
        await loadData();
        // Find and update the selected message with the new data
        const updatedMessages = await fetch(`/api/v1/admin/support/messages?status=${statusFilter}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }).then(res => res.ok ? res.json() : null);

        if (updatedMessages?.messages) {
          const updatedMessage = updatedMessages.messages.find((msg: SupportMessage) => msg.id === messageId);
          if (updatedMessage) {
            setSelectedMessage(updatedMessage);
          }
        }
        addNotification(language === 'ua' ? 'Відповідь успішно надіслано клієнту!' : 'Reply sent to client successfully!', 'success');
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      addNotification(language === 'ua' ? 'Помилка при надсиланні відповіді' : 'Error sending reply', 'error');
    }
  };



  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    if (language === 'ua') {
      // Ukrainian format: "7 січ. 2026 р., 13:18"
      const months = [
        'січ.', 'лют.', 'бер.', 'квіт.', 'трав.', 'черв.',
        'лип.', 'серп.', 'вер.', 'жовт.', 'лист.', 'груд.'
      ];

      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day} ${month} ${year} р., ${hours}:${minutes}`;
    } else {
      // English format
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };



  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return '#e74c3c';
      case 'in_progress': return '#f39c12';
      case 'resolved': return '#27ae60';
      case 'closed': return '#95a5a6';
      default: return '#6c757d';
    }
  };



  // Load settings
  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/admin/support/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const settings = await response.json();
        setSupportSettings(settings);
      }
    } catch (error) {
      console.error('Error loading support settings:', error);
    }
  };

  // Update settings
  const updateSettings = async () => {
    setIsUpdatingSettings(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/admin/support/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(supportSettings),
      });

      if (response.ok) {
        addNotification(language === 'ua' ? 'Налаштування підтримки успішно оновлено!' : 'Support settings updated successfully!', 'success');
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating support settings:', error);
      addNotification(language === 'ua' ? 'Помилка при оновленні налаштувань' : 'Error updating settings', 'error');
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  // Load data on component mount and when filter changes
  useEffect(() => {
    loadData();
    loadSettings();
  }, [statusFilter]);

  if (loading) {
    return (
      <div className="admin-support-page">
        {/* <div className="page-header">
          <h1>{language === 'ua' ? 'Звернення клієнтів' : 'Customer Messages'}</h1>
        </div> */}
        <div className="loading">
          {language === 'ua' ? 'Завантаження...' : 'Loading...'}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-support-page">
      <div className="settings-card">
        <div className="page-header">
          <h1>
            <i className="bi bi-headset"></i>
            {language === 'ua' ? 'Підтримка клієнтів' : 'Customer Support'}
          </h1>
          <p className="page-subtitle">
            {language === 'ua'
              ? 'Управління зверненнями клієнтів та налаштуваннями підтримки'
              : 'Manage customer inquiries and support settings'
            }
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="admin-tabs-navigation">
        <button
          className={`admin-tab-button ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <i className="bi bi-envelope"></i>
          <span className="tab-text">
            {language === 'ua' ? 'Звернення' : 'Customer Appeals'}
          </span>
        </button>
        <button
          className={`admin-tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <i className="bi bi-gear"></i>
          <span className="tab-text">{language === 'ua' ? 'Налаштування' : 'Settings'}</span>
        </button>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <>
          {/* Statistics Cards */}
          <div className="stats-grid">
            <div
              className={`stat-card total ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              <div className="stat-icon total">
                <i className="bi bi-collection"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">{language === 'ua' ? 'Всі' : 'All'}</div>
              </div>
            </div>

            <div
              className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? '' : 'new')}
            >
              <div className="stat-icon new">
                <i className="bi bi-envelope"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.new}</div>
                <div className="stat-label">{language === 'ua' ? 'Нові' : 'New'}</div>
              </div>
            </div>

            <div
              className={`stat-card ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? '' : 'in_progress')}
            >
              <div className="stat-icon in-progress">
                <i className="bi bi-clock"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.in_progress}</div>
                <div className="stat-label">{language === 'ua' ? 'В роботі' : 'In Progress'}</div>
              </div>
            </div>

            <div
              className={`stat-card ${statusFilter === 'resolved' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'resolved' ? '' : 'resolved')}
            >
              <div className="stat-icon resolved">
                <i className="bi bi-check-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.resolved}</div>
                <div className="stat-label">{language === 'ua' ? 'Вирішені' : 'Resolved'}</div>
              </div>
            </div>

            <div
              className={`stat-card ${statusFilter === 'closed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'closed' ? '' : 'closed')}
            >
              <div className="stat-icon closed">
                <i className="bi bi-x-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.closed}</div>
                <div className="stat-label">{language === 'ua' ? 'Закриті' : 'Closed'}</div>
              </div>
            </div>
          </div>

          {/* Messages Table */}
          <div className="messages-section">
            {messages.length === 0 ? (
              <div className="no-messages">
                <i className="bi bi-envelope-x"></i>
                <p>
                  {language === 'ua'
                    ? 'Немає повідомлень за вибраними критеріями'
                    : 'No messages found for selected criteria'
                  }
                </p>
              </div>
            ) : (
              <div className="messages-table-container">
                <table className="messages-table">
                  <thead>
                    <tr>
                      <th>{language === 'ua' ? 'Дата' : 'Date'}</th>
                      <th>{language === 'ua' ? 'Відправник' : 'Sender'}</th>
                      <th>{language === 'ua' ? 'Категорія' : 'Category'}</th>
                      <th>{language === 'ua' ? 'Повідомлення' : 'Message'}</th>
                      <th>
                        <div className="status-header">
                          <span>{language === 'ua' ? 'Статус' : 'Status'}</span>
                          <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="status-filter-inline"
                          >
                            <option value="">{language === 'ua' ? 'Всі' : 'All'}</option>
                            <option value="new">{language === 'ua' ? 'Нові' : 'New'}</option>
                            <option value="in_progress">{language === 'ua' ? 'В роботі' : 'In Progress'}</option>
                            <option value="resolved">{language === 'ua' ? 'Вирішені' : 'Resolved'}</option>
                            <option value="closed">{language === 'ua' ? 'Закриті' : 'Closed'}</option>
                          </select>
                        </div>
                      </th>
                      <th>{language === 'ua' ? 'Дії' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((message) => (
                      <tr key={message.id} className="message-row">
                        <td className="date-cell">{formatDate(message.created_at)}</td>
                        <td className="sender-cell">
                          <div className="sender-info">
                            <div className="sender-name">
                              {message.user_name}
                              <span className="user-role-badge" title={language === 'ua' ? `Роль: ${message.user_role}` : `Role: ${message.user_role}`}>
                                ({message.user_role})
                              </span>
                            </div>
                            <div className="sender-contact">
                              {message.user_email && <span>{message.user_email}</span>}
                              {message.user_phone && <span>{message.user_phone}</span>}
                            </div>
                            {message.all_businesses && message.all_businesses.length > 0 && (
                              <div className="business-tooltip-container">
                                <span
                                  className="business-name"
                                  title={message.all_businesses.map(b =>
                                    `${b.name}${b.city ? ` (${b.city})` : ''}${b.address ? ` - ${b.address}` : ''}`
                                  ).join('\n')}
                                >
                                  <i className="bi bi-building"></i>
                                  {message.business_name || message.all_businesses[0]?.name}
                                  {message.all_businesses.length > 1 && (
                                    <span className="business-count">+{message.all_businesses.length - 1}</span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="category-cell">{message.category}</td>
                        <td className="message-cell">
                          <div className="message-preview">
                            {message.message.length > 100
                              ? `${message.message.substring(0, 100)}...`
                              : message.message
                            }
                          </div>
                        </td>
                        <td className="status-cell">
                          <select
                            value={message.status}
                            onChange={(e) => handleQuickStatusUpdate(message.id, e.target.value)}
                            className="status-select-table"
                            style={{ backgroundColor: getStatusColor(message.status) }}
                          >
                            <option value="new">{language === 'ua' ? 'Нове' : 'New'}</option>
                            <option value="in_progress">{language === 'ua' ? 'В роботі' : 'In Progress'}</option>
                            <option value="resolved">{language === 'ua' ? 'Вирішено' : 'Resolved'}</option>
                            <option value="closed">{language === 'ua' ? 'Закрито' : 'Closed'}</option>
                          </select>
                        </td>
                        <td className="actions-cell">
                          <Badge
                            badgeContent={countUnreadClientMessagesForThread(message)}
                            color="primary"
                            invisible={countUnreadClientMessagesForThread(message) === 0}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.6rem',
                                minWidth: '16px',
                                height: '16px',
                              },
                            }}
                          >
                            <button
                              className="view-btn-table"
                              onClick={() => setSelectedMessage(message)}
                              title={language === 'ua' ? 'Переглянути повідомлення' : 'View message'}
                            >
                              <i className="bi bi-chat-dots"></i>
                            </button>
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="settings-section">
          <h2>{language === 'ua' ? 'Налаштування підтримки' : 'Support Settings'}</h2>
          <div className="settings-form">
            <div className="form-group">
              <label htmlFor="supportPhone">{language === 'ua' ? 'Номер телефону підтримки' : 'Support Phone Number'}</label>
              <input
                id="supportPhone"
                type="text"
                value={supportSettings.phone}
                onChange={(e) => setSupportSettings(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+38 (044) 123-45-67"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="supportEmail">{language === 'ua' ? 'Email підтримки' : 'Support Email'}</label>
              <input
                id="supportEmail"
                type="email"
                value={supportSettings.email}
                onChange={(e) => setSupportSettings(prev => ({ ...prev, email: e.target.value }))}
                placeholder="support@visit-platform.com"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="workingHours">{language === 'ua' ? 'Час роботи' : 'Working Hours'}</label>
              <input
                id="workingHours"
                type="text"
                value={supportSettings.working_hours}
                onChange={(e) => setSupportSettings(prev => ({ ...prev, working_hours: e.target.value }))}
                placeholder="Пн-Пт: 9:00-18:00"
                className="form-input"
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={updateSettings}
                disabled={isUpdatingSettings}
                className="save-btn"
              >
                {isUpdatingSettings
                  ? (language === 'ua' ? 'Збереження...' : 'Saving...')
                  : (language === 'ua' ? 'Зберегти налаштування' : 'Save Settings')
                }
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Support Chat Modal */}
      <SupportChatModal
        open={!!selectedMessage}
        message={selectedMessage}
        onClose={() => setSelectedMessage(null)}
        onMessageSent={() => loadData()}
        isAdmin={true}
        onSendReply={handleSendReply}
      />
    </div>
  );
};

export default AdminSupportPage;
